import { useCallback, useEffect, useRef, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { postLiveMove, postLivePages } from '@api-client/liveMoves';
import { POLL_COPY } from '@components/polls/pollCopy';
import { toast } from '@hooks/use-toast';
import { liveHostKey } from '@lib/cache/polls';
import { LIVE_POLL_COPY } from '@components/livePolls/livePollCopy';
import { LIVE_ERROR_CODES, liveStateFromRefusal, pollErrorCode } from '@lib/polls/pollErrors';
import type { LiveHostState, LiveMoveKind, LivePagesAction, LiveState } from '@models/api';
import { writeLiveMoveIntoQuestionCaches, writeLiveStateIntoCaches } from './writeLiveState';

type LiveRequest =
  | { kind: 'move'; move: LiveMoveKind; from: LiveState }
  | { kind: 'pages'; action: LivePagesAction };

export interface LiveMoves {
  /** Start, Next, Back or End from the state the host sees. False when one is already in flight (ignored). */
  move: (move: LiveMoveKind, from: LiveState) => boolean;
  /** Pause, resume or skip the results pages. */
  page: (action: LivePagesAction) => boolean;
  /** What is in flight, if anything: every control holds until it settles. */
  pending: LiveMoveKind | LivePagesAction | null;
}

/**
 * The author's moves on one live poll, shared by every surface that drives it (the big screen's B2b/B3c here,
 * FE-L1b's host controls). TECH-867 § Decided "The author's laptop can run the poll too":
 *
 * - every move sends `expected_position` and `expected_live_state` (CONTRACT § 4.9, the 08:55 note), so a
 *   phone and a laptop, a double press or a retry on bad wifi never skip a question or undo an End;
 * - one at a time: a press while one is in flight is ignored (a ref, so two key presses in one tick count once);
 * - never retried: a 409 with the fresh state (`LIVE_POSITION_MISMATCH`, `LIVE_MOVE_INVALID`, and
 *   `LIVE_POLL_ARCHIVED` with its toast) is shown as is;
 *   any other failure is a toast, and the next press is safe because of the expected state;
 * - not optimistic: the room's screen must never show a move the server did not make. The answer is written
 *   into the caches at once (`writeLiveStateIntoCaches`), so the author's screen moves without waiting for the
 *   next 2 s read.
 */
export interface UseLiveMovesOptions {
  /** After the server made the move (the host view: Start puts the poll in its place's feed). */
  onMoved?: (move: LiveMoveKind | LivePagesAction, host: LiveHostState) => void;
}

export function useLiveMoves(code: string, options: UseLiveMovesOptions = {}): LiveMoves {
  const queryClient = useQueryClient();
  const onMoved = useRef(options.onMoved);
  useEffect(() => {
    onMoved.current = options.onMoved;
  });
  const inFlight = useRef(false);
  const [pending, setPending] = useState<LiveMoves['pending']>(null);

  const mutation = useMutation<LiveHostState, unknown, LiveRequest>({
    mutationFn: async request => {
      const response =
        request.kind === 'move'
          ? await postLiveMove(code, request.move, {
              expected_position: request.from.current_position,
              expected_live_state: request.from.live_state,
            })
          : await postLivePages(code, { action: request.action, expected_live_state: 'ended' });
      return response.data;
    },
    retry: false,
    onSuccess: async (host, request) => {
      await writeLiveStateIntoCaches(queryClient, code, host.state, host);
      // Every surface's moves: the author's card and Your live polls follow (FE-L1b review).
      writeLiveMoveIntoQuestionCaches(queryClient, host);
      onMoved.current?.(request.kind === 'move' ? request.move : request.action, host);
    },
    onError: async error => {
      const fresh = liveStateFromRefusal(error);
      if (fresh) {
        // The poll moved from another surface (or was archived): its question caches follow too, so the author's
        // feed card is not left on the old state. The question id comes from the host read (the host view, L9b);
        // the big screen holds none and has no card on that page.
        const questionId = queryClient.getQueryData<LiveHostState>(liveHostKey(code))?.question_id;
        await writeLiveStateIntoCaches(queryClient, code, fresh);
        if (questionId)
          writeLiveMoveIntoQuestionCaches(queryClient, { question_id: questionId, state: fresh });
        // Back or Reopen once retention made the guest answers anonymous (BE-L3-fix2): said, never retried.
        if (pollErrorCode(error) === LIVE_ERROR_CODES.archived) {
          toast({
            title: LIVE_POLL_COPY.liveArchived,
            variant: 'info',
            id: `live-archived:${code}`,
          });
        }
        return;
      }
      toast({
        title: POLL_COPY.somethingWentWrong,
        variant: 'error',
        id: `live-move-failed:${code}`,
      });
    },
    onSettled: () => {
      inFlight.current = false;
      setPending(null);
    },
  });
  const { mutate } = mutation;

  const send = useCallback(
    (request: LiveRequest): boolean => {
      if (inFlight.current) return false;
      inFlight.current = true;
      setPending(request.kind === 'move' ? request.move : request.action);
      mutate(request);
      return true;
    },
    [mutate],
  );

  const move = useCallback(
    (kind: LiveMoveKind, from: LiveState) => send({ kind: 'move', move: kind, from }),
    [send],
  );
  const page = useCallback((action: LivePagesAction) => send({ kind: 'pages', action }), [send]);

  return { move, page, pending };
}
